import { ref, computed } from 'vue'
import client from '@/graphql/client'
import GetOrgUsageForOwner from '@/graphql/queries/GetOrgUsageForOwner'
import ListOrgProjects from '@/graphql/queries/ListOrgProjects'

// Owner-facing org AI usage rows (cost-free) + the aggregation helpers shared by the
// "LLM Usage" and "Org Insights" dashboards. One fetch per dashboard instance; both
// read the same getOrgUsageForOwner data (model, useCase, projectId, userId, tokens).
export const useOrgUsage = (orgId) => {
  const rangeDays = ref(30)
  const rangeOptions = [
    { label: 'Last 7 days', value: 7 },
    { label: 'Last 30 days', value: 30 },
    { label: 'Last 90 days', value: 90 }
  ]
  const rows = ref([])
  const loading = ref(false)
  const projectNames = ref({}) // projectId -> name, so charts show names not UUIDs

  const computeRange = (days) => {
    const to = new Date()
    to.setHours(0, 0, 0, 0)
    to.setDate(to.getDate() + 1)
    const from = new Date()
    from.setHours(0, 0, 0, 0)
    from.setDate(from.getDate() - days + 1)

    return { from: from.toISOString(), to: to.toISOString() }
  }

  const fetchProjectNames = async () => {
    try {
      const res = await client.query({
        operationName: 'listOrgProjects',
        query: ListOrgProjects,
        variables: { orgId }
      })
      const map = {}
      for (const p of (res.listOrgProjects || [])) {
        if (p?.id) {
          map[p.id] = p.name || p.id
        }
      }
      projectNames.value = map
    } catch (e) {
      console.error(e)
    }
  }

  const fetchUsage = async () => {
    if (!orgId) {
      return
    }
    loading.value = true
    try {
      const { from, to } = computeRange(rangeDays.value)
      // Page through all rows (resolver returns 1000/page) so totals aren't truncated.
      const all = []
      let nextToken = null
      do {
        const res = await client.query({
          operationName: 'getOrgUsageForOwner',
          query: GetOrgUsageForOwner,
          variables: { orgId, from, to, nextToken }
        })
        const page = res.getOrgUsageForOwner || {}
        all.push(...(page.items || []))
        nextToken = page.nextToken
      } while (nextToken)
      rows.value = all
    } catch (e) {
      console.error(e)
      rows.value = []
    }
    loading.value = false
  }

  const load = async () => {
    await Promise.all([fetchProjectNames(), fetchUsage()])
  }

  const totals = computed(() => {
    const r = rows.value
    const users = new Set(r.map((x) => x.userId).filter(Boolean))

    return {
      calls: r.length,
      tokens: r.reduce((s, x) => s + (x.totalTokens || 0), 0),
      users: users.size
    }
  })

  // Sum tokens grouped by a row key, sorted by token volume desc. labelFn maps the raw
  // key to a display label (e.g. a useCase slug -> "Entities", a projectId -> name).
  const tokensGroupedBy = (keyFn, labelFn) => {
    const map = {}
    for (const row of rows.value) {
      const k = keyFn(row) || 'unknown'
      if (!map[k]) {
        map[k] = { key: k, label: labelFn ? labelFn(k) : k, tokens: 0 }
      }
      map[k].tokens += row.totalTokens || 0
    }

    return Object.values(map).sort((a, b) => b.tokens - a.tokens)
  }

  return { rangeDays, rangeOptions, rows, loading, projectNames, totals, tokensGroupedBy, load, fetchUsage }
}
